import jsonToFormData from 'json-form-data';

import api from '../config/api';

import { formatErrorResponse } from '../utils/response';
import { IOS_PLATFORM } from '../utils/platforms';

// Private functions

async function createProduct(product) {
  const data = { product };
  const response = await api.post('/products', data);
  return response;
}

async function uploadProductImages(id, product) {
  const config = {
    headers: { 'content-type': 'multipart/form-data' },
  };
  const data = jsonToFormData({ product });
  const response = await api.post(`/products/${id}/image_set`, data, config);
  return response.ok;
}

async function uploadProductBundle(id, product) {
  const config = {
    headers: { 'content-type': 'multipart/form-data' },
  };
  const data = jsonToFormData({ product });
  const response = await api.post(`/products/${id}/mesh`, data, config);
  return response.ok;
}

function returnWithError(response, { created = false } = {}) {
  return {
    created,
    ok: false,
    error: response.data ? response.data.message : response.problem,
  };
}

// Public functions

export async function createNewProduct(productInfo, imagesForm, bundleForm) {
  const productResponse = await createProduct(productInfo);
  if (!productResponse.ok) {
    return returnWithError(productResponse);
  }
  const product = productResponse.data;
  const photosCreated = await uploadProductImages(product.id, imagesForm);
  const bundleCreated = await uploadProductBundle(product.id, bundleForm);
  if (!photosCreated || !bundleCreated) {
    return {
      product,
      ok: true,
      partial: true,
    };
  }
  return {
    product,
    ok: true,
    partial: false,
  };
}

export async function createNewProductQR(id) {
  const data = {};
  const response = await api.post(`/products/${id}/qr`, data);
  if (response.ok) {
    return {
      ok: true,
      url: response.data.qr,
    };
  }
  return formatErrorResponse(response);
}

export async function getProductDetails(id, { owner = null } = {}) {
  const params = owner ? { owner } : {};
  const response = await api.get(`/products/${id}/details`, {}, { params });
  if (response.ok) {
    return {
      ok: true,
      product: response.data,
    };
  }
  return formatErrorResponse(response);
}

export async function updateProduct(id, product) {
  const response = await api.post(`/products/${id}`, { product });
  if (response.ok) {
    return {
      ok: true,
      product: response.data,
    };
  }
  return formatErrorResponse(response);
}

export async function updateProductImage(id, { file, index }) {
  const config = {
    headers: { 'content-type': 'multipart/form-data' },
  };
  const data = jsonToFormData({ photo: { file } });
  const response = await api.post(`/products/${id}/photos/${index}`, data, config);
  if (response.ok) {
    return {
      ok: true,
      image: response.data,
    };
  }
  return formatErrorResponse(response);
}

export async function updateProductBundle(id, { mesh, platform }) {
  const config = {
    headers: { 'content-type': 'multipart/form-data' },
  };
  const product = {};
  if (platform === IOS_PLATFORM) {
    product.ios_bundle = mesh;
  } else {
    product.android_bundle = mesh;
  }
  const data = jsonToFormData({ product });
  const response = await api.post(`/products/${id}/mesh`, data, config);
  if (response.ok) {
    return {
      ok: true,
      image: response.data,
    };
  }
  return formatErrorResponse(response);
}

export async function assignProduct(id, data) {
  const override = { ...data, product_id: id };
  const response = await api.post('/product_overrides', { override });
  if (response.ok) {
    return {
      ok: true,
      product: response.data,
    };
  }
  return formatErrorResponse(response);
}

export async function updateProductOverride(id, override) {
  const response = await api.patch(`/product_overrides/${id}`, { override });
  if (response.ok) {
    return {
      ok: true,
      product: response.data,
    };
  }
  return formatErrorResponse(response);
}

export async function dissociateProduct(id) {
  const response = await api.delete(`/product_overrides/${id}`);
  if (response.ok) {
    return {
      ok: true,
      product: response.data,
    };
  }
  return formatErrorResponse(response);
}

export async function deleteProduct(id) {
  const response = await api.delete(`/products/${id}`);
  return response.ok;
}



// WEBPACK FOOTER //
// ./src/services/ProductServices.js